/**
 * ReferralErrorFallback Component
 *
 * Error display component with retry functionality for referral landing page.
 * Provides user-friendly error messages and recovery options.
 */

import React from 'react';
import { useTheme } from '../../hooks/useTheme';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { TitleText } from '../ui/TitleText';
import { BodyText } from '../ui/BodyText';
import type { ReferralErrorFallbackProps } from '../../types/referralLanding';

/**
 * ReferralErrorFallback component for displaying errors with recovery options
 *
 * @param props - ReferralErrorFallback component props
 * @returns JSX.Element
 */
export const ReferralErrorFallback: React.FC<ReferralErrorFallbackProps> = ({
  error,
  onRetry,
  className = '',
}) => {
  const { isDark } = useTheme();

  const cardBgColor = isDark ? 'bg-neutral-800' : 'bg-white';
  const borderColor = isDark ? 'border-neutral-700' : 'border-neutral-200';

  /**
   * Get error icon and color based on error type
   */
  const getErrorDisplay = (errorType: string) => {
    switch (errorType) {
      case 'not_found':
        return {
          icon: <NotFoundIcon className="w-16 h-16 text-yellow-500" />,
          color: 'text-yellow-600',
          dotColor: 'bg-yellow-600',
          bgColor: isDark ? 'bg-yellow-900/20' : 'bg-yellow-50',
          borderColor: isDark ? 'border-yellow-700' : 'border-yellow-200',
        };
      case 'expired':
        return {
          icon: <ExpiredIcon className="w-16 h-16 text-orange-500" />,
          color: 'text-orange-600',
          dotColor: 'bg-orange-600',
          bgColor: isDark ? 'bg-orange-900/20' : 'bg-orange-50',
          borderColor: isDark ? 'border-orange-700' : 'border-orange-200',
        };
      case 'invalid_token':
        return {
          icon: <InvalidIcon className="w-16 h-16 text-red-500" />,
          color: 'text-red-600',
          dotColor: 'bg-red-600',
          bgColor: isDark ? 'bg-red-900/20' : 'bg-red-50',
          borderColor: isDark ? 'border-red-700' : 'border-red-200',
        };
      case 'network_error':
        return {
          icon: <NetworkIcon className="w-16 h-16 text-blue-500" />,
          color: 'text-blue-600',
          dotColor: 'bg-blue-600',
          bgColor: isDark ? 'bg-blue-900/20' : 'bg-blue-50',
          borderColor: isDark ? 'border-blue-700' : 'border-blue-200',
        };
      default:
        return {
          icon: <ServerErrorIcon className="w-16 h-16 text-gray-500" />,
          color: 'text-gray-600',
          dotColor: 'bg-gray-600',
          bgColor: isDark ? 'bg-gray-900/20' : 'bg-gray-50',
          borderColor: isDark ? 'border-gray-700' : 'border-gray-200',
        };
    }
  };

  /**
   * Get user-friendly error title
   */
  const getErrorTitle = (errorType: string): string => {
    switch (errorType) {
      case 'not_found':
        return 'Referral Not Found';
      case 'expired':
        return 'Referral Expired';
      case 'invalid_token':
        return 'Invalid Referral Link';
      case 'network_error':
        return 'Connection Problem';
      default:
        return 'Something Went Wrong';
    }
  };

  /**
   * Get user-friendly error description
   */
  const getErrorDescription = (errorType: string): string => {
    switch (errorType) {
      case 'not_found':
        return 'The referral link you\'re trying to access could not be found. It may have been removed or the link might be incorrect.';
      case 'expired':
        return 'This referral link has expired and is no longer valid. Please contact your healthcare provider for a new referral.';
      case 'invalid_token':
        return 'The referral link appears to be invalid or corrupted. Please check the link and try again.';
      case 'network_error':
        return 'We\'re having trouble connecting to our servers. Please check your internet connection and try again.';
      default:
        return 'An unexpected error occurred while loading your referral information. Please try again or contact support if the problem persists.';
    }
  };

  /**
   * Get suggested actions based on error type
   */
  const getSuggestedActions = (errorType: string): string[] => {
    switch (errorType) {
      case 'not_found':
        return [
          'Double-check the referral link from your healthcare provider',
          'Contact your physician\'s office for a new referral link',
          'Ensure you\'re using the complete link without any missing characters'
        ];
      case 'expired':
        return [
          'Contact your healthcare provider for a new referral',
          'Ask your physician to send an updated referral link',
          'Check if you have any other recent referral emails'
        ];
      case 'invalid_token':
        return [
          'Copy and paste the complete link from your email',
          'Try opening the link in a different browser',
          'Contact your healthcare provider if the link continues to fail'
        ];
      case 'network_error':
        return [
          'Check your internet connection',
          'Try refreshing the page',
          'Wait a moment and try again'
        ];
      default:
        return [
          'Try refreshing the page',
          'Clear your browser cache and cookies',
          'Contact support if the problem continues'
        ];
    }
  };

  const errorDisplay = getErrorDisplay(error.type);
  const showRetryButton = error.type === 'network_error' || error.type === 'server_error';

  return (
    <Card className={`${cardBgColor} ${borderColor} ${className}`}>
      <div className="p-8 text-center">
        {/* Error icon */}
        <div className={`
          inline-flex items-center justify-center w-24 h-24 rounded-full mb-6
          ${errorDisplay.bgColor} ${errorDisplay.borderColor} border-2
        `}>
          {errorDisplay.icon}
        </div>

        {/* Error title */}
        <TitleText level={2} className="mb-4">
          {getErrorTitle(error.type)}
        </TitleText>

        {/* Error description */}
        <BodyText variant="secondary" className="mb-6 max-w-md mx-auto">
          {getErrorDescription(error.type)}
        </BodyText>

        {/* Technical error message (if available) */}
        {error.message && error.message !== getErrorDescription(error.type) && (
          <div className={`
            p-3 rounded-lg mb-6 text-left
            ${isDark ? 'bg-neutral-750' : 'bg-neutral-100'}
          `}>
            <BodyText size="sm" variant="secondary" className="font-mono">
              Error: {error.message}
            </BodyText>
            {error.code && (
              <BodyText size="sm" variant="secondary" className="font-mono mt-1">
                Code: {error.code}
              </BodyText>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="space-y-4 mb-6">
          {showRetryButton && (
            <Button
              onClick={onRetry}
              variant="primary"
              size="lg"
              className="w-full sm:w-auto"
            >
              <RefreshIcon className="w-5 h-5 mr-2" />
              Try Again
            </Button>
          )}

          <Button
            onClick={() => window.location.href = '/'}
            variant="outline"
            size="md"
            className="w-full sm:w-auto"
          >
            <HomeIcon className="w-4 h-4 mr-2" />
            Go to Homepage
          </Button>
        </div>

        {/* Suggested actions */}
        <div className="text-left">
          <BodyText size="sm" className="font-medium mb-3">
            What you can do:
          </BodyText>
          <ul className="space-y-2">
            {getSuggestedActions(error.type).map((action, index) => (
              <li key={index} className="flex items-start space-x-2">
                <div className={`
                  w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0
                  ${errorDisplay.dotColor}
                `} />
                <BodyText size="sm" variant="secondary">
                  {action}
                </BodyText>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  );
};

/**
 * Icon components for different error types
 */
const NotFoundIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.562M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const ExpiredIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const InvalidIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

const NetworkIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
  </svg>
);

const ServerErrorIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.562M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const RefreshIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const HomeIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

export default ReferralErrorFallback;